import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { hasAIScore, getBandScore, hasTeacherReviewedScore, shouldShowScore, parseAnalytics } from './utils/scoreHelpers';

/**
 * Helper function to get correct/incorrect counts
 * Prefers backend-provided counts, falls back to calculating from analytics
 * @param {Object} apiResponse - API response object with correctCount, incorrectCount, analytics
 * @param {Object} analytics - Analytics object from API response (fallback)
 * @param {number} totalQuestions - Total number of questions in the test (fallback)
 */
function getCorrectIncorrectCounts(apiResponse, analytics = null, totalQuestions = null) {
  const backendTotalQuestions = apiResponse && typeof apiResponse.totalQuestions === 'number'
    ? apiResponse.totalQuestions
    : null;
  const expectedTotalQuestions = totalQuestions !== null ? totalQuestions : backendTotalQuestions;
  const backendTotalMismatch = totalQuestions !== null
    && backendTotalQuestions !== null
    && backendTotalQuestions !== totalQuestions;

  // If backend provides counts directly, use them (preferred)
  if (apiResponse && typeof apiResponse.correctCount === 'number' && typeof apiResponse.incorrectCount === 'number') {
    return { 
      correct: apiResponse.correctCount, 
      incorrect: apiResponse.incorrectCount,
      incomplete: backendTotalMismatch || (expectedTotalQuestions !== null && expectedTotalQuestions > 0
        ? apiResponse.correctCount + apiResponse.incorrectCount !== expectedTotalQuestions
        : false),
    };
  }
  
  // Fallback: calculate from analytics if backend didn't provide counts
  let correct = 0;
  let incorrect = 0;
  
  if (!analytics) {
    return { correct, incorrect, incomplete: totalQuestions !== null && totalQuestions > 0 };
  }

  analytics = parseAnalytics(analytics);
  
  // Count correct answers from analytics
  Object.values(analytics).forEach(item => {
    if (item && typeof item === 'object' && item.hasOwnProperty('isCorrect')) {
      if (item.isCorrect) {
        correct++;
      } else {
        incorrect++;
      }
    }
  });
  
  if (totalQuestions !== null && totalQuestions > 0) {
    return {
      correct,
      incorrect,
      incomplete: correct + incorrect !== totalQuestions,
    };
  }
  
  return { correct, incorrect, incomplete: false };
}

/**
 * Reusable IELTS Band Score Display Component
 * 
 * @param {Object} props
 * @param {Object} props.apiResult - API result object with response containing totalMarksObtained and analytics
 * @param {boolean} props.showCorrectIncorrect - Whether to show correct/incorrect counts (default: false, true for Reading/Listening)
 * @param {boolean} props.showOnlyWhenScoreAvailable - Whether to only show when score is available (default: false)
 * @param {number} props.totalQuestions - Total number of questions in the test (required for accurate incorrect count including unanswered)
 * @param {boolean} props.preferManualScore - Whether teacher/manual score should override AI score
 */
const IELTSBandScore = ({ 
  apiResult, 
  showCorrectIncorrect = false,
  showOnlyWhenScoreAvailable = false,
  totalQuestions = null,
  preferManualScore = false
}) => {
  if (!apiResult || !apiResult.response) return null;

  const { analytics, status, scoringScope, accuracyPercentage } = apiResult.response;
  const isPartialPractice = scoringScope === 'selected_part_practice';
  
  // If showOnlyWhenScoreAvailable is true, check if score is available (handles 0 correctly)
  if (showOnlyWhenScoreAvailable) {
    if (!shouldShowScore(apiResult)) return null;
  }

  // Get band score using helper (handles 0 correctly)
  const bandScore = getBandScore(apiResult, { preferManualScore });
  if (!isPartialPractice && bandScore === null && !hasAIScore(apiResult)) return null;

  // Use backend-provided counts if available, otherwise calculate from analytics
  const counts = showCorrectIncorrect ? getCorrectIncorrectCounts(apiResult.response, analytics, totalQuestions) : null;
  const showTeacherReviewedBadge = preferManualScore && hasTeacherReviewedScore(apiResult);

  if (counts?.incomplete) {
    return (
      <Box sx={{ width: '100%', mb: 2, p: 3, borderRadius: 3, backgroundColor: '#FFF8E1' }}>
        <Typography variant="h6" sx={{ color: '#9A6700', fontWeight: 700 }}>
          Result incomplete
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B5A1E', mt: 0.5 }}>
          Some questions were not included in this saved result.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      mb: 2,
      p: 3,
      borderRadius: 3,
      boxShadow: '0 4px 24px rgba(153,108,254,0.10)',
      background: 'linear-gradient(90deg, #F8F2FF 60%, #E8DFFF 100%)',
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: 'center',
      justifyContent: showCorrectIncorrect ? 'space-between' : 'center',
      gap: 3,
      boxSizing: 'border-box'
    }}>
      <Box sx={{ flex: showCorrectIncorrect ? 1 : 'none', textAlign: { xs: 'center', sm: showCorrectIncorrect ? 'left' : 'center' } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#996CFE', mb: 1 }}>
          {isPartialPractice
            ? `Practice accuracy: ${Number(accuracyPercentage) || 0}%`
            : `🎉 Your Band: ${bandScore !== null ? bandScore : 'N/A'}`}
        </Typography>
        {showTeacherReviewedBadge && (
          <Chip
            label="Teacher Reviewed"
            size="small"
            sx={{
              mb: 1,
              backgroundColor: '#E8F5E9',
              color: '#2E7D32',
              border: '1px solid rgba(46, 125, 50, 0.28)',
              fontWeight: 700
            }}
          />
        )}
        {status === 'pending_evaluation' && (
          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', mt: 1 }}>
            Pending evaluation by examiner
          </Typography>
        )}
      </Box>
      
      {showCorrectIncorrect && counts && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: { xs: 2, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#37965E', fontSize: 32 }} />
            <Typography variant="h6" sx={{ color: '#37965E', fontWeight: 700 }}>
              {counts.correct} Correct
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: '#E53935', fontSize: 32 }} />
            <Typography variant="h6" sx={{ color: '#E53935', fontWeight: 700 }}>
              {counts.incorrect} Incorrect
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IELTSBandScore;
export { getCorrectIncorrectCounts };
